/**
 * Home Service
 * Aggregates dashboard data from other modules
 */

import { calendarService } from '@/modules/calendar/service';
import { fitnessService } from '@/modules/fitness/service';
import { studiumService } from '@/modules/studium/service';
import { toolsService } from '@/modules/tools/service';
import type { CalendarEventsRecord, HomeworkRecord, ExerciseLogsRecord } from '@/types/collections';
import {
  buildDashboardSummary,
  buildQuickStats,
  type DashboardSummary,
  type QuickStats,
} from './selectors';
import { homeReadRepository } from './repository';

export type { DashboardSummary, QuickStats } from './selectors';

export type WeatherCard = {
  temp: number;
  condition: string;
  message: string;
};

class HomeService {
  setUserId(userId: string): void {
    homeReadRepository.setUserId(userId);
    calendarService.setUserId(userId);
    fitnessService.setUserId(userId);
    studiumService.setUserId(userId);
    toolsService.setUserId(userId);
  }

  /**
   * Get dashboard summary for current user
   * Aggregates data from all modules
   */
  async getDashboardSummary(): Promise<DashboardSummary> {
    try {
      const [upcomingEvents, pendingHomework, recentWorkouts, recentGrades] =
        await Promise.all([
          homeReadRepository.listUpcomingEvents(),
          homeReadRepository.listPendingHomework(),
          homeReadRepository.listRecentWorkouts(7),
          homeReadRepository.listRecentGrades(),
        ]);

      return buildDashboardSummary({
        upcomingEventsCount: upcomingEvents.records.length,
        pendingHomeworkCount: pendingHomework.records.length,
        recentWorkoutsCount: recentWorkouts.records.length,
        recentGrades: recentGrades.records,
      });
    } catch (error) {
      console.error('[Home] getDashboardSummary error:', error);
      throw error;
    }
  }

  /**
   * Get quick stats for dashboard
   */
  async getQuickStats(): Promise<QuickStats> {
    try {
      const [upcomingEvents, pendingHomework, exerciseLogs] = await Promise.all(
        [
          homeReadRepository.listUpcomingEvents(),
          homeReadRepository.listPendingHomework(),
          homeReadRepository.listRecentWorkouts(30),
        ]
      );

      return buildQuickStats({
        upcomingEventsCount: upcomingEvents.records.length,
        pendingAssignmentsCount: pendingHomework.records.length,
        exerciseLogs: exerciseLogs.records,
      });
    } catch (error) {
      console.error('[Home] getQuickStats error:', error);
      throw error;
    }
  }

  /**
   * Get weather data (placeholder for future integration)
   */
  async getWeather(): Promise<WeatherCard> {
    return {
      temp: 20,
      condition: 'Cloudy',
      message: 'Weather integration coming soon',
    };
  }

  async createQuickCalendarEvent(data: {
    title: string;
    startDate: string;
    endDate: string;
    description?: string;
  }) {
    return calendarService.createEvent({
      title: data.title,
      start_date: data.startDate,
      end_date: data.endDate,
      startDate: data.startDate,
      endDate: data.endDate,
      description: data.description,
      category: 'personal',
      is_all_day: false,
      isAllDay: false,
    } as Partial<CalendarEventsRecord>);
  }

  async createQuickStudyTask(data: {
    title: string;
    subject: string;
    dueDate: string;
    priority: 'low' | 'medium' | 'high';
  }) {
    return studiumService.createAssignment({
      title: data.title,
      subject: data.subject,
      due_date: data.dueDate,
      priority: data.priority,
      completed: false,
    } as Partial<HomeworkRecord>);
  }

  async createQuickWorkout(data: {
    date: string;
    exerciseType: string;
    duration: number;
    intensity: 'low' | 'medium' | 'high';
  }) {
    return fitnessService.logExercise({
      date: data.date,
      exercise_type: data.exerciseType,
      duration: data.duration,
      intensity: data.intensity,
    } as Partial<ExerciseLogsRecord>);
  }

  async createQuickDeck(data: { name: string; description?: string }) {
    return toolsService.createDeck({
      name: data.name,
      description: data.description,
      cardCount: 0,
    });
  }
}

export const homeService = new HomeService();

